import { useReduxState } from "SubjectStandardsV2/hooks";
import _ from "lodash";
import { getDateMomentObjectCustom } from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { useRef } from "react";
import { getRepeatSettingsOrNull } from "../modules/Utils";

/**
 * Selector to get event modal data from Redux state
 * @param {Object} state - Redux state
 * @returns {Object} Object containing eventModal data
 */
const getEventDetailsSelector = state => {
  return {
    eventModal: _.get(state, "myCalendar.eventModal", ""),
  };
};

/**
 * Custom hook to determine if only reminders have been updated in the event modal
 * Compares the current modal data with the initial data to check if changes are limited to reminders only
 *
 * @param {Object|null} selectedTimeSlot - Optional time slot object containing startDate, endDate, startTime, and endTime
 * @returns {boolean} Returns true if only reminders were updated and no other fields changed, false otherwise
 */
const useIsOnlyRemindersUpdated = (selectedTimeSlot = null) => {
  const { eventModal } = useReduxState(getEventDetailsSelector);
  const {
    eventTitle,
    eventType,
    startDate,
    endDate,
    isRepeatEvent,
    isAllDayEvent,
    participants,
    location,
    description,
    attachments,
    reminders,
    sharedWith,
    entityType,
    entityId,
    repeatEventSettings,
    shouldNotifyParticipants,
    skipNonOperationalDays,
  } = eventModal;
  const { startTime, endTime } = selectedTimeSlot || {};
  const startDateTime = selectedTimeSlot
    ? getDateMomentObjectCustom(
        `${selectedTimeSlot?.startDate} ${startTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm
      )
    : null;
  const endDateTime = selectedTimeSlot
    ? getDateMomentObjectCustom(
        `${selectedTimeSlot?.endDate} ${endTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm
      )
    : null;
  const modalData = {
    eventTitle,
    eventType,
    startDate,
    endDate,
    isRepeatEvent,
    isAllDayEvent,
    participants,
    location,
    description,
    attachments,
    reminders,
    sharedWith,
    entityType,
    entityId,
    repeatEventSettings: getRepeatSettingsOrNull({
      repeatEventSettings,
      isRepeatEvent,
    }),
    shouldNotifyParticipants,
    skipNonOperationalDays,
  };

  const oldDataRef = useRef({
    ...modalData,
    startDate: startDateTime || startDate,
    endDate: endDateTime || endDate,
  });

  if (!oldDataRef.current.entityType) {
    oldDataRef.current.entityType = entityType;
  }

  const isRemindersUpdated = !_.isEqual(
    oldDataRef.current.reminders,
    modalData.reminders
  );
  const isOtherDataUpdated = !_.isEqual(
    _.omit(oldDataRef.current, "reminders"),
    _.omit(modalData, "reminders")
  );

  return isRemindersUpdated && !isOtherDataUpdated;
};

export default useIsOnlyRemindersUpdated;
